import axios from "axios";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';
import { FINE_GROUP, BONUS_GROUP } from "@/constants/fineAndBonusGroups";

const apiUrl = "/index.php";

const reportTypeParameters = {
    "by-reasons": 1,
    "by-workers": 2,
    "by-reasons-and-workers": 3,
};

const fineOrBonusType = {
    bonuses: 1,
    fines: 0,
};

const getQueryParamsFromFilters = (filters) => {
    const queryParams = [];

    const {
        startDate,
        endDate,
        filials,
        positions,
        filialsExcept,
        positionsExcept,
        selectedFinesOrBonuses,
        reportType,
        reportTypeItems,
        sortType,
        sortKey,
        shiftType,
    } = filters;

    if (startDate && endDate) {
        queryParams.push(`start=${startDate}&end=${endDate}&period=custom`);
    }
    if (filials.length) {
        const filialIds = filials.map((f) => (filialsExcept ? `-${f.value}` : f.value)).join(",");
        queryParams.push(`filial_id=${filialIds}`);
    }
    if (positions.length) {
        positions.forEach((position) => {
            queryParams.push(`posts[]=${position.value}`);
        });
        if (positionsExcept) {
            queryParams.push("exclPosts=1");
        }
    }
    if (reportType) {
        queryParams.push(`report_type=${reportTypeParameters[reportType]}`);
    }
    if (reportTypeItems.length) {
        reportTypeItems.forEach((item) => {
            if (reportTypeParameters[reportType] === 1) {
                queryParams.push(`worker_id[]=${item.value}`);
            }
            if (reportTypeParameters[reportType] === 2) {
                queryParams.push(`bonus_fine_id[]=${item.value}`);
            }
            if (reportTypeParameters[reportType] === 3) {
                queryParams.push(`category_id[]=${item.value === "empty" ? null : item.value}`);
            }
        });
    }
    if (selectedFinesOrBonuses) {
        queryParams.push(`types=${fineOrBonusType[selectedFinesOrBonuses]}`);
    }
    if (sortType && sortKey) {
        const key = reportTypeParameters[reportType] === 2 ? "sort" : "sort_by_key";
        queryParams.push(`${key}=${sortType === "desc" ? `-${sortKey}` : sortKey} `);
    }

    if (shiftType && shiftType.value) {
        queryParams.push(`smena=${shiftType.value}`);
    }

    return queryParams;
};

const state = () => ({
    reportRecords: [],
    collections: {
        positions: [],
        shiftTypes: [],
        filials: [],
        workers: [],
        categories: {
            fine: [],
            bonus: [],
        },
        reasons: {
            fine: [],
            bonus: [],
        },
    },
    queryForGoogleSheets: "",
});

const getters = {
    noReportRecordsFound(state) {
        return state.reportRecords.all_categories_report
            ? state.reportRecords.all_categories_report.length === 0
            : state.reportRecords.length === 0;
    },
};

const actions = {
    async fetchReportRecords({ rootState, commit }, filters) {
        const token = rootState.authToken;
        const queryParams = getQueryParamsFromFilters(filters).join("&");

        commit("setQueryForGoogleSheets", queryParams);

        const url = addAuthKey(`${apiUrl}?r=api/bonus-penalty-report/index&${queryParams}`, token);
        const { data } = await axios.get(url);

        commit("setReportRecords", data);
    },
    async fetchPositions({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/position`, token);

        const { data } = await axios.get(url);
        const positions = Object.entries(data).map(([positionCode, { name }]) => ({
            label: name,
            value: positionCode,
        }));

        commit("setPositions", positions);
    },
    async fetchFilials({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/filial/index`, token);

        const { data } = await axios.get(url);
        const filials = data.items.map((filial) => ({
            label: filial.filial_name,
            value: filial.id,
        }));

        commit("setFilials", filials);
    },
    async fetchShiftTypes({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/smena`, token);

        const { data } = await axios.get(url);

        commit("setShiftTypes", data);
    },
    async fetchWorkers({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker/index&pagination=0&sort=name&job_status=1&for_filter=1`, token);

        const { data } = await axios.get(url);
        const workers = data.map((worker) => ({
            label: worker.name,
            value: worker.id,
        }));

        commit("setWorkers", workers);
    },
    async fetchBonusFineCategories({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/bonus-fine-category/index`, token);

        const { data } = await axios.get(url);

        data.push({
            name: "Без категории",
            id: "empty",
            type: FINE_GROUP,
        });
        data.push({
            name: "Без категории",
            id: "empty",
            type: BONUS_GROUP,
        });

        commit("setCategories", data);
    },
    async fetchBonusFineReasons({ rootState, commit }) {
        const token = rootState.authToken;

        const response = await Promise.all([
            axios.get(addAuthKey(`${apiUrl}?r=api/fine/index&active=1`, token)),
            axios.get(addAuthKey(`${apiUrl}?r=api/bonus/index&active=1`, token)),
        ]);

        commit(
            "setReasons",
            response.map((res) => res.data),
        );
    },
};

const mutations = {
    setReportRecords(state, records) {
        state.reportRecords = records;
    },
    setPositions(state, positions) {
        state.collections.positions = positions;
    },
    setFilials(state, filials) {
        state.collections.filials = filials;
    },
    setShiftTypes(state, shiftTypes) {
        state.collections.shiftTypes = Object.entries(shiftTypes).map(([value, label]) => ({ value, label }));
    },
    setWorkers(state, workers) {
        state.collections.workers = workers;
    },
    setCategories(state, categories) {
        categories.forEach((category) => {
            if (category.type === FINE_GROUP) {
                state.collections.categories.fine.push({
                    label: category.name,
                    value: category.id,
                    name: `fine-group-${category.id}`,
                });
            }
            if (category.type === BONUS_GROUP) {
                state.collections.categories.bonus.push({
                    label: category.name,
                    value: category.id,
                    name: `bonus-group-${category.id}`,
                });
            }
        });
    },
    setReasons(state, reasons) {
        const [fineReasons, bonusReasons] = reasons;
        state.collections.reasons = {
            fine: fineReasons.map((reason) => ({
                label: reason.description,
                value: reason.id,
                group: `fine-group-${reason.category_id ? reason.category_id : "empty"}`,
                categoryId: reason.category_id,
            })),
            bonus: bonusReasons.map((reason) => ({
                label: reason.description,
                value: reason.id,
                group: `bonus-group-${reason.category_id ? reason.category_id : "empty"}`,
                categoryId: reason.category_id,
            })),
        };
    },
    setQueryForGoogleSheets(state, query) {
        state.queryForGoogleSheets = query;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
